import type { DpiaHistoryList, DpiaRequestOpenResponse } from 'config/api/dpiaRequests/dpiaRequests.types';
import type { DpiaContext, DpiaDetail } from 'config/api/dpias/dpias.types';

const pickString = (...values: Array<string | null | undefined>): string => {
  const match = values.find(value => typeof value === 'string' && value.trim());
  return typeof match === 'string' ? match.trim() : '';
};

export const resolveSourceDpiaRequestId = (
  detailSourceId: number | null | undefined,
  history: DpiaHistoryList | null | undefined,
  currentDpiaId: number,
): number | null => {
  if (detailSourceId) {
    return detailSourceId;
  }

  if (!history?.items.length) {
    return null;
  }

  const dpiaIndex = history.items.findIndex(item => item.kind === 'dpia' && item.id === currentDpiaId);
  const relevantItems = dpiaIndex >= 0 ? history.items.slice(0, dpiaIndex + 1) : history.items;
  const requests = relevantItems.filter(item => item.kind === 'dpia_request');
  const requiredRequest = [...requests].reverse().find(item => item.status === 'decided_dpia_required');

  return requiredRequest?.id ?? requests[requests.length - 1]?.id ?? null;
};

export const formatDpiaBannerDate = (value?: string | null): string => {
  const trimmed = value?.trim();

  if (!trimmed) {
    return '';
  }

  const date = new Date(trimmed);

  if (Number.isNaN(date.getTime())) {
    return trimmed.slice(0, 10);
  }

  return date.toISOString().slice(0, 10);
};

export type DpiaBannerMeta = {
  hasSourceRequest: boolean;
  initiatedDate: string;
  responsibleOwner: string;
  sourceDpiaRequestId: number | null;
};

export const resolveDpiaBannerMeta = ({
  context,
  detail,
  history,
  sourceRequest,
}: {
  detail: DpiaDetail;
  context: DpiaContext;
  history?: DpiaHistoryList | null;
  sourceRequest?: DpiaRequestOpenResponse | null;
}): DpiaBannerMeta => {
  const sourceDpiaRequestId = resolveSourceDpiaRequestId(detail.sourceDpiaRequestId, history, detail.id);
  const request = sourceRequest?.request;

  const responsibleOwner = pickString(
    detail.responsibleName,
    sourceRequest?.context.assetOwnerName,
    context.operationalOwnerName,
    detail.updatedByName,
  );

  const initiatedDate = formatDpiaBannerDate(pickString(detail.initiatedAt, request?.assessedAt, request?.updatedAt, detail.updatedAt));

  return {
    hasSourceRequest: Boolean(sourceDpiaRequestId),
    initiatedDate,
    responsibleOwner,
    sourceDpiaRequestId,
  };
};
